import { Phone, MessageCircle, ShieldCheck, Star, ArrowRight, MapPin, ImagePlay, Camera } from "lucide-react";
import { Link } from "@/components/common/Link";

/**
 * ⚠️ 10Web AI WARNING — DO NOT REPLACE <Icon /> JSX with <img> tags.
 * All lucide icons here are wrapped with data-icon-container="lucide-do-not-replace".
 * Keep them as React components.
 */

/* ==========================================================================
   IMAGE URLS — UPDATE THESE AFTER UPLOADING TO 10WEB MEDIA LIBRARY
   ==========================================================================
   Suggested upload format: WebP, 1200×900 main / 600×450 thumbnail
   Naming convention: gallery-hero-{description}-{suburb}.webp
   ========================================================================== */

const HERO_IMAGES = [{
  src: "https://goldgaragedoor.com.au/wp-content/uploads/2026/06/gallery-hero-1-sectional-bondi.webp",
  alt: "Sectional garage door installation in Bondi, Sydney by Gold Garage Door",
  suburb: "Bondi",
  tag: "Sectional install"
}, {
  src: "https://goldgaragedoor.com.au/wp-content/uploads/2026/06/gallery-hero-2-insulated-drummoyne.webp",
  alt: "Insulated B&D Panelift door installed in Drummoyne",
  suburb: "Drummoyne",
  tag: "Insulated"
}, {
  src: "https://goldgaragedoor.com.au/wp-content/uploads/2026/06/gallery-hero-3-roller-parramatta.webp",
  alt: "Roller garage door fitted in Parramatta home",
  suburb: "Parramatta",
  tag: "Roller door"
}, {
  src: "https://goldgaragedoor.com.au/wp-content/uploads/2026/06/gallery-hero-4-before-after-cronulla.webp",
  alt: "Before and after garage door upgrade in Cronulla",
  suburb: "Cronulla",
  tag: "Before / After"
}];
const CATEGORY_CHIPS = [{
  label: "Before & After",
  anchor: "#before-after"
}, {
  label: "Sectional",
  anchor: "#by-type"
}, {
  label: "Roller",
  anchor: "#by-type"
}, {
  label: "Insulated",
  anchor: "#by-type"
}, {
  label: "Repairs",
  anchor: "#by-type"
}];
const NAVY = "#081438";
const NAVY_DEEP = "#050D24";
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const CREAM = "#FAF7F1";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
export default function GalleryHero() {
  return <section id="gallery-top" className="relative overflow-hidden" style={{
    backgroundColor: NAVY,
    fontFamily: FONT_BODY,
    paddingTop: "120px",
    paddingBottom: "100px"
  }}>
      {/* Ambient radial */}
      <div aria-hidden className="absolute inset-0" style={{
      background: `radial-gradient(circle at 0% 0%, ${NAVY_DEEP} 0%, ${NAVY} 50%), radial-gradient(circle at 100% 100%, ${GOLD}11 0%, transparent 40%)`
    }} />

      {/* Grain */}
      <div aria-hidden className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E\")"
    }} />

      {/* Top gold scanline */}
      <div aria-hidden className="absolute top-0 left-0 right-0 h-px" style={{
      background: `linear-gradient(90deg, transparent, ${GOLD} 50%, transparent)`
    }} />

      {/* Watermark "Gallery" wordmark */}
      <div aria-hidden className="absolute pointer-events-none select-none" style={{
      left: "-20px",
      bottom: "-60px",
      fontFamily: FONT_HEAD,
      fontSize: "clamp(8rem, 16vw, 20rem)",
      fontWeight: 900,
      lineHeight: 0.9,
      letterSpacing: "-0.06em",
      color: `${GOLD}0E`
    }}>
        Gallery
      </div>

      <div className="relative max-w-[1320px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-[1fr_1.05fr] gap-12 lg:gap-16 items-center">
          {/* LEFT — copy + CTAs */}
          <div className="text-white">
            {/* Eyebrow */}
            <div className="inline-flex items-center gap-2 mb-7 px-4 py-2 rounded-full border" style={{
            borderColor: `${GOLD}55`,
            backgroundColor: `${GOLD}10`
          }}>
              <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                <Camera size={14} color={GOLD} strokeWidth={2.5} />
              </span>
              <span style={{
              fontFamily: FONT_HEAD,
              fontSize: "12px",
              fontWeight: 700,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: GOLD
            }}>
                Project Gallery · Sydney
              </span>
            </div>

            {/* H1 */}
            <h1 style={{
            fontFamily: FONT_HEAD,
            fontWeight: 800,
            fontSize: "clamp(2.5rem, 5.5vw, 4.7rem)",
            lineHeight: 1.02,
            letterSpacing: "-0.03em",
            marginBottom: "20px"
          }}>
              1,247 doors.
              <br />
              <span style={{
              color: GOLD
            }}>
                Every Sydney suburb.
              </span>
              <br />
              One family crew.
            </h1>

            {/* Sub */}
            <p style={{
            fontFamily: FONT_BODY,
            fontSize: "clamp(1.05rem, 1.4vw, 1.2rem)",
            lineHeight: 1.6,
            color: "rgba(255,255,255,0.82)",
            maxWidth: "540px",
            marginBottom: "28px"
          }}>
              Seventeen years of Sydney installs, repairs, before-and-afters,
              and signature builds. Browse the work, find the suburb you live
              in, and book the same hands for yours.
            </p>

            {/* Category chips */}
            <div className="mb-9">
              <div style={{
              fontFamily: FONT_HEAD,
              fontSize: "10px",
              fontWeight: 700,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.5)",
              marginBottom: "12px"
            }}>
                Jump to a category
              </div>
              <div className="flex flex-wrap gap-2">
                {CATEGORY_CHIPS.map(c => <a key={c.label} href={c.anchor} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all hover:scale-[1.04]" style={{
                backgroundColor: "rgba(255,255,255,0.06)",
                border: `1px solid rgba(245,166,35,0.25)`,
                color: "rgba(255,255,255,0.85)",
                fontFamily: FONT_HEAD,
                fontWeight: 700,
                fontSize: "12px",
                textDecoration: "none"
              }}>
                    {c.label}
                    <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex" style={{
                  color: GOLD
                }}>
                      <ArrowRight size={11} strokeWidth={2.5} />
                    </span>
                  </a>)}
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap items-center gap-4 mb-9">
              <a href="tel:0485000933" className="inline-flex items-center gap-2.5 px-7 py-4 rounded-full transition-all hover:scale-[1.02] hover:shadow-xl" style={{
              backgroundColor: GOLD,
              color: NAVY,
              fontFamily: FONT_HEAD,
              fontWeight: 700,
              fontSize: "16px",
              boxShadow: `0 10px 30px ${GOLD}33`
            }}>
                <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                  <Phone size={18} strokeWidth={2.5} />
                </span>
                Book your install
                <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                  <ArrowRight size={16} strokeWidth={2.5} />
                </span>
              </a>

              <a href="https://wa.me/61485000933?text=Hi%2C%20I%20saw%20your%20gallery%20and%20would%20like%20a%20quote." target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2.5 px-6 py-4 rounded-full border-2 transition-all hover:scale-[1.02]" style={{
              borderColor: "#25D366",
              backgroundColor: "#25D366",
              color: "#081438",
              fontFamily: FONT_HEAD,
              fontWeight: 700,
              fontSize: "15px"
            }}>
                <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                  <MessageCircle size={18} strokeWidth={2.5} />
                </span>
                WhatsApp
              </a>
            </div>

            {/* Trust strip */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 pt-6 border-t" style={{
            borderColor: "rgba(255,255,255,0.12)"
          }}>
              <div className="flex items-center gap-2">
                <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                  <ShieldCheck size={16} color={GOLD} strokeWidth={2.5} />
                </span>
                <span style={{
                fontFamily: FONT_BODY,
                fontSize: "13px",
                color: "rgba(255,255,255,0.78)",
                fontWeight: 600
              }}>
                  1-Year Labour Warranty
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex gap-0.5">
                  {[0, 1, 2, 3, 4].map(i => <span key={i} aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                      <Star size={14} color={GOLD} fill={GOLD} strokeWidth={0} />
                    </span>)}
                </div>
                <span style={{
                fontFamily: FONT_BODY,
                fontSize: "13px",
                color: "rgba(255,255,255,0.78)",
                fontWeight: 600
              }}>
                  5.0 · 200+ reviews
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                  <MapPin size={16} color={GOLD} strokeWidth={2.5} />
                </span>
                <span style={{
                fontFamily: FONT_BODY,
                fontSize: "13px",
                color: "rgba(255,255,255,0.78)",
                fontWeight: 600
              }}>
                  217+ suburbs covered
                </span>
              </div>
            </div>
          </div>

          {/* RIGHT — image bento mosaic */}
          <div className="relative">
            {/* "Latest work" floating chip */}
            <div className="absolute z-10 inline-flex items-center gap-2 px-3.5 py-2 rounded-full" style={{
            top: "-14px",
            left: "16px",
            background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_DARK} 100%)`,
            color: NAVY,
            fontFamily: FONT_HEAD,
            fontSize: "10.5px",
            fontWeight: 900,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            boxShadow: `0 8px 24px ${GOLD}55`
          }}>
              <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                <ImagePlay size={12} strokeWidth={2.75} />
              </span>
              Latest installs
            </div>

            {/* Bento grid 2x2 asymmetric */}
            <div className="grid gap-3" style={{
            gridTemplateColumns: "1.3fr 1fr",
            gridTemplateRows: "1.15fr 1fr"
          }}>
              {/* Image 1 — large hero (spans both rows on left) */}
              <a href="#showcase" className="relative rounded-2xl overflow-hidden group" style={{
              gridRow: "1 / span 2",
              aspectRatio: "auto",
              backgroundColor: "#1a2440",
              boxShadow: "0 20px 60px rgba(0,0,0,0.45)",
              border: `1px solid ${GOLD}33`
            }}>
                <img src={HERO_IMAGES[0].src} alt={HERO_IMAGES[0].alt} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" style={{
                minHeight: "440px"
              }} loading="eager" src="https://goldgaragedoor.com.au/wp-content/uploads/2026/06/PHOTO-2026-06-23-10-44-32.webp" />
                <div aria-hidden className="absolute inset-0 pointer-events-none" style={{
                background: "linear-gradient(180deg, transparent 50%, rgba(8,20,56,0.85) 100%)"
              }} />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full mb-2" style={{
                  backgroundColor: `${GOLD}EE`,
                  color: NAVY,
                  fontFamily: FONT_HEAD,
                  fontSize: "9.5px",
                  fontWeight: 900,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase"
                }}>
                    {HERO_IMAGES[0].tag}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                      <MapPin size={12} color="#ffffff" strokeWidth={2.5} />
                    </span>
                    <span style={{
                    fontFamily: FONT_HEAD,
                    fontSize: "13px",
                    fontWeight: 700,
                    color: "#ffffff",
                    letterSpacing: "-0.01em"
                  }}>
                      {HERO_IMAGES[0].suburb}
                    </span>
                  </div>
                </div>
              </a>

              {/* Image 2 — top right */}
              <a href="#showcase" className="relative rounded-2xl overflow-hidden group" style={{
              backgroundColor: "#1a2440",
              boxShadow: "0 12px 36px rgba(0,0,0,0.35)",
              border: `1px solid rgba(255,255,255,0.08)`
            }}>
                <img src={HERO_IMAGES[1].src} alt={HERO_IMAGES[1].alt} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.05]" style={{
                minHeight: "210px"
              }} loading="eager" src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1681701/img/image2-hero-mobile.webp" src="https://goldgaragedoor.com.au/wp-content/uploads/2026/06/IMG_7556-scaled.jpg" />
                <div aria-hidden className="absolute inset-0 pointer-events-none" style={{
                background: "linear-gradient(180deg, transparent 55%, rgba(8,20,56,0.85) 100%)"
              }} />
                <div className="absolute bottom-0 left-0 right-0 p-3.5">
                  <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full mb-1.5" style={{
                  backgroundColor: `${GOLD}EE`,
                  color: NAVY,
                  fontFamily: FONT_HEAD,
                  fontSize: "8.5px",
                  fontWeight: 900,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase"
                }}>
                    {HERO_IMAGES[1].tag}
                  </div>
                  <div style={{
                  fontFamily: FONT_HEAD,
                  fontSize: "11px",
                  fontWeight: 700,
                  color: "#ffffff"
                }}>
                    {HERO_IMAGES[1].suburb}
                  </div>
                </div>
              </a>

              {/* Image 3 — bottom right */}
              <a href="#showcase" className="relative rounded-2xl overflow-hidden group" style={{
              backgroundColor: "#1a2440",
              boxShadow: "0 12px 36px rgba(0,0,0,0.35)",
              border: `1px solid rgba(255,255,255,0.08)`
            }}>
                <img src={HERO_IMAGES[2].src} alt={HERO_IMAGES[2].alt} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.05]" style={{
                minHeight: "180px"
              }} loading="eager" src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1681701/img/9ada9b93-909d-463a-a8f5-c673b2a6de54.png" src="https://goldgaragedoor.com.au/wp-content/uploads/2026/06/98e6b5da-5f28-4e7a-9644-2bf8351ab70d.webp" />
                <div aria-hidden className="absolute inset-0 pointer-events-none" style={{
                background: "linear-gradient(180deg, transparent 55%, rgba(8,20,56,0.85) 100%)"
              }} />
                <div className="absolute bottom-0 left-0 right-0 p-3.5">
                  <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full mb-1.5" style={{
                  backgroundColor: `${GOLD}EE`,
                  color: NAVY,
                  fontFamily: FONT_HEAD,
                  fontSize: "8.5px",
                  fontWeight: 900,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase"
                }}>
                    {HERO_IMAGES[2].tag}
                  </div>
                  <div style={{
                  fontFamily: FONT_HEAD,
                  fontSize: "11px",
                  fontWeight: 700,
                  color: "#ffffff"
                }}>
                    {HERO_IMAGES[2].suburb}
                  </div>
                </div>
              </a>
            </div>

            {/* Floating "see all" badge */}
            <a href="#showcase" className="absolute z-10 inline-flex items-center gap-2 px-4 py-2.5 rounded-full transition-all hover:scale-[1.04]" style={{
            bottom: "-18px",
            right: "16px",
            backgroundColor: NAVY_DEEP,
            color: "#ffffff",
            fontFamily: FONT_HEAD,
            fontSize: "12px",
            fontWeight: 700,
            letterSpacing: "0.04em",
            border: `1px solid ${GOLD}55`,
            boxShadow: `0 12px 28px rgba(0,0,0,0.4), 0 0 0 4px ${NAVY}`,
            textDecoration: "none"
          }}>
              See all 50+ projects
              <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex" style={{
              color: GOLD
            }}>
                <ArrowRight size={14} strokeWidth={2.5} />
              </span>
            </a>
          </div>
        </div>
      </div>

      {/* Bottom gold scanline */}
      <div aria-hidden className="absolute bottom-0 left-0 right-0 h-px" style={{
      background: `linear-gradient(90deg, transparent, ${GOLD}66 50%, transparent)`
    }} />
    </section>;
}